/**
 * Field History API — tracks per-field edit history.
 *
 * Each content field's history is stored under:
 *   {siteId}:__fhist:{contentKey} → FieldHistoryEntry[]
 *
 * Max 20 entries per field (oldest pruned on write).
 */

import { apiFetch, scopeKey } from "../components/shared/ContentEditingContext";

const FHIST_PREFIX = "__fhist:";
const MAX_ENTRIES = 20;

export interface FieldHistoryEntry {
  value: string;
  timestamp: number;
  user?: string;
}

/** Check if a content key is a field history key (for filtering). */
export function isFieldHistoryKey(key: string): boolean {
  return key.startsWith(FHIST_PREFIX);
}

/** Get the KV key for a field's history. */
function histKey(contentKey: string): string {
  return FHIST_PREFIX + contentKey;
}

/**
 * Record a change to a field's history.
 * Debounced externally — caller should not call this on every keystroke.
 */
export async function recordFieldChange(
  siteId: string,
  contentKey: string,
  oldValue: string,
  _newValue: string,
  username?: string,
): Promise<void> {
  try {
    const serverKey = scopeKey(histKey(contentKey), siteId);

    // Read existing history
    const data = await apiFetch(`/content/${encodeURIComponent(siteId)}`);
    const raw: Record<string, any> = data.content || {};
    const existing: FieldHistoryEntry[] = Array.isArray(raw[serverKey]) ? raw[serverKey] : [];

    // Don't record if oldValue is the same as the last entry
    if (existing.length > 0 && existing[0].value === oldValue) {
      return;
    }

    // Add new entry at the beginning
    const entry: FieldHistoryEntry = {
      value: oldValue,
      timestamp: Date.now(),
      user: username,
    };

    const updated = [entry, ...existing].slice(0, MAX_ENTRIES);

    await apiFetch("/content/bulk", {
      method: "PUT",
      body: JSON.stringify({
        overrides: { [serverKey]: updated },
      }),
    });
  } catch (err) {
    console.error("[fieldHistoryApi] Failed to record field change:", err);
  }
}

/**
 * Get the history for a specific field.
 */
export async function getFieldHistory(
  siteId: string,
  contentKey: string,
): Promise<FieldHistoryEntry[]> {
  try {
    const serverKey = scopeKey(histKey(contentKey), siteId);
    const data = await apiFetch(`/content/${encodeURIComponent(siteId)}`);
    const raw: Record<string, any> = data.content || {};
    const history = raw[serverKey];
    if (Array.isArray(history)) return history;
    return [];
  } catch (err) {
    console.error("[fieldHistoryApi] Failed to get field history:", err);
    return [];
  }
}